import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { SERVICES, COMING_SOON_SLUGS, servicePagePath } from "@/lib/content";
import { priceInfoFor } from "@/lib/servicesContent";
import { fmtMoney } from "@/lib/promo";
import { api } from "@/lib/api";

export default function Services() {
  const [priceMap, setPriceMap] = useState({});
  useEffect(() => {
    // Single source of truth: the pricing engine config (same as calculator)
    api.get("/pricing/config").then(({ data }) => {
      const map = {};
      SERVICES.forEach((s) => { map[s.slug] = priceInfoFor(s.slug, data.services || [], data.promo); });
      setPriceMap(map);
    }).catch(() => {});
  }, []);

  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 md:py-36"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16 md:mb-20">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="w-8 h-px bg-[#1F5EFF]" />
              <span className="font-mono-alt text-[12px] text-[#1F5EFF]">
                What We Do
              </span>
            </div>
            <h2 className="font-display text-4xl md:text-6xl font-light tracking-tight text-white max-w-3xl">
              The full menu.{" "}
              <span className="text-white/50">
                Zero compromises on execution.
              </span>
            </h2>
          </div>
          <p className="text-white/50 max-w-md text-sm md:text-base">
            Every service is delivered in a controlled, dust-free studio using
            the highest-tier films, chemicals and computer-cut templates.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {SERVICES.map((svc, i) => {
            const Icon = svc.icon;
            const soon = COMING_SOON_SLUGS.includes(svc.slug);
            return (
              <motion.div
                key={svc.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: (i % 4) * 0.06, ease: [0.22, 1, 0.36, 1] }}
              >
                <Link
                  to={servicePagePath(svc.slug)}
                  data-testid={`service-card-${svc.slug}`}
                  className={`card-hover relative bg-[#17171c] border rounded-2xl p-7 md:p-8 flex flex-col group overflow-hidden h-full ${soon ? "border-white/8" : "border-white/12"}`}
                >
                  <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-[#1F5EFF]/0 group-hover:bg-[#1F5EFF]/8 blur-3xl transition-all duration-700" />
                  {soon && (
                    <span
                      data-testid={`service-soon-${svc.slug}`}
                      className="absolute top-5 right-5 px-2.5 py-1 rounded-full border border-amber-400/30 bg-amber-400/10 text-amber-300 text-[9px] font-mono-alt uppercase tracking-widest"
                    >
                      Coming Soon
                    </span>
                  )}
                  <div className="flex items-center justify-between">
                    <div className={`w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center transition-all duration-500 ${soon ? "text-white/40" : "text-[#1F5EFF] group-hover:bg-[#1F5EFF] group-hover:text-white"}`}>
                      <Icon className="w-5 h-5" strokeWidth={1.5} />
                    </div>
                    <span className="font-mono-alt text-[9px] text-white/40">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <h3 className="mt-8 font-display text-xl md:text-2xl text-white leading-snug">
                    {svc.title}
                  </h3>
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-mono-alt text-white/40">
                      {svc.tag}
                    </span>
                    {!soon && priceMap[svc.slug]?.from != null && (
                      <>
                        <span className="text-white/20">·</span>
                        {priceMap[svc.slug].promoFrom != null ? (
                          <>
                            <span className="text-[10px] font-mono-alt text-white/35 line-through">
                              From {fmtMoney(priceMap[svc.slug].from)}
                            </span>
                            <span className="text-[10px] font-mono-alt text-emerald-300" data-testid={`service-promo-price-${svc.slug}`}>
                              From {fmtMoney(priceMap[svc.slug].promoFrom)}
                            </span>
                          </>
                        ) : (
                          <span className="text-[10px] font-mono-alt text-[#1F5EFF]">
                            From {fmtMoney(priceMap[svc.slug].from)}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                  <p className="mt-5 text-sm text-white/55 leading-relaxed flex-1">
                    {svc.short}
                  </p>
                  <div
                    className="mt-6 inline-flex items-center gap-2 text-[13px] text-white/80 group-hover:text-[#1F5EFF] transition-colors"
                    data-testid={`service-cta-${svc.slug}`}
                  >
                    {soon ? "Join the Waitlist" : "Learn More"}
                    <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
